/**
 * DXF-style assoc-list ⇄ app entity mapping (P4). entmake/entget speak the
 * AutoCAD group-code dialect; the app entity data bags are the source of
 * truth. Supported set: LINE, CIRCLE, ARC, LWPOLYLINE, POINT, TEXT, MTEXT —
 * everything else is an honest error (help subset table documents it).
 */

import { LispError } from './errors';
import { mkList, mkReal, mkStr, type LispValue } from './values';
import { useAppStore, type Entity } from '../../state/store';
import { makeEntity } from '../commands/entityOps';

// ---------------------------------------------------------------------------
// Assoc-list reading
// ---------------------------------------------------------------------------

type Pair = { code: number; nums: number[]; str: string | null; dotted: LispValue | null };

function readPairs(list: LispValue): Pair[] {
    if (list.k !== 'list' && list.k !== 'nil') throw new LispError('bad argument type: expected an assoc list');
    const pairs: Pair[] = [];
    if (list.k !== 'list') return pairs;
    for (const item of list.items) {
        if (item.k !== 'list' || item.items.length === 0) {
            throw new LispError('malformed assoc list entry');
        }
        const codeVal = item.items[0];
        if (codeVal.k !== 'int' && codeVal.k !== 'real') {
            throw new LispError('malformed assoc list entry (group code)');
        }
        const code = Math.trunc(codeVal.v);
        const rest = item.items.slice(1);
        const dotted = item.dot ?? null;
        const nums = rest.filter((v): v is { k: 'int' | 'real'; v: number } => v.k === 'int' || v.k === 'real').map((v) => v.v);
        const strItem = rest.find((v) => v.k === 'str');
        pairs.push({
            code,
            nums,
            str: dotted?.k === 'str' ? dotted.v : strItem?.k === 'str' ? strItem.v : null,
            dotted,
        });
    }
    return pairs;
}

function pairNum(pairs: Pair[], code: number): number | null {
    const hit = pairs.find((p) => p.code === code);
    if (!hit) return null;
    if (hit.nums.length > 0) return hit.nums[0];
    if (hit.dotted && (hit.dotted.k === 'int' || hit.dotted.k === 'real')) return hit.dotted.v;
    return null;
}

function pairStr(pairs: Pair[], code: number): string | null {
    const hit = pairs.find((p) => p.code === code);
    return hit?.str ?? null;
}

function pairPoint(pairs: Pair[], code: number): [number, number, number] | null {
    const hit = pairs.find((p) => p.code === code);
    if (!hit || hit.nums.length < 2) return null;
    return [hit.nums[0], hit.nums[1], hit.nums[2] ?? 0];
}

/** Classic AutoCAD Color Index → CSS hex (1-9 plus 250-255 grayscale). */
const ACI_COLORS: Record<number, string> = {
    1: '#ff0000', 2: '#ffff00', 3: '#00ff00', 4: '#00ffff',
    5: '#0000ff', 6: '#ff00ff', 7: '#ffffff', 8: '#808080', 9: '#c0c0c0',
};

function aciToHex(index: number): string {
    if (ACI_COLORS[index]) return ACI_COLORS[index];
    if (index >= 250 && index <= 255) {
        const level = Math.round(((index - 250) / 5) * 255);
        const hex = level.toString(16).padStart(2, '0');
        return `#${hex}${hex}${hex}`;
    }
    return '#ffffff';
}

/** Reverse of aciToHex for entget; null when the color has no ACI form. */
export function hexToAci(hex: unknown): number | null {
    if (typeof hex !== 'string') return null;
    const upper = hex.toUpperCase();
    for (const [aci, value] of Object.entries(ACI_COLORS)) {
        if (value.toUpperCase() === upper) return Number(aci);
    }
    if (/^#[0-9a-f]{6}$/i.test(upper)) {
        const r = parseInt(upper.slice(1, 3), 16);
        if (r === parseInt(upper.slice(3, 5), 16) && r === parseInt(upper.slice(5, 7), 16)) {
            const gray = Math.round((r / 255) * 5) + 250;
            return gray;
        }
    }
    return null;
}

export interface EntityBuild {
    type: string;
    data: Record<string, unknown>;
    layerId?: string;
    name: string;
}

/**
 * Build an app entity shape from a DXF-style assoc list. Angles arrive in
 * degrees (DXF 50/51) and are stored in radians (the app's convention).
 */
export function assocListToEntityBuild(list: LispValue): EntityBuild {
    const pairs = readPairs(list);
    const type = (pairStr(pairs, 0) ?? '').toUpperCase();
    if (!type) throw new LispError('entmake: missing entity type (group 0)');

    const colorIndex = pairNum(pairs, 62);
    const color = colorIndex && colorIndex > 0 ? aciToHex(colorIndex) : null;

    let layerId: string | undefined;
    const layerName = pairStr(pairs, 8);
    if (layerName) {
        const app = useAppStore.getState();
        const layer = Object.values(app.layers).find(
            (l) => l.name.toLowerCase() === layerName.toLowerCase()
        );
        if (!layer) throw new LispError(`entmake: unknown layer "${layerName}"`);
        layerId = layer.id;
    }

    const point = (code: number): [number, number, number] => {
        const p = pairPoint(pairs, code);
        if (!p) throw new LispError(`entmake ${type}: missing group ${code}`);
        return p;
    };
    const num = (code: number, what: string): number => {
        const v = pairNum(pairs, code);
        if (v === null) throw new LispError(`entmake ${type}: missing group ${code} (${what})`);
        return v;
    };
    const degToRad = (deg: number) => (deg * Math.PI) / 180;

    switch (type) {
        case 'LINE': {
            const data: Record<string, unknown> = { start: point(10), end: point(11) };
            if (color) data.color = color;
            return { type: 'line', data, layerId, name: `Line-${Date.now()}` };
        }
        case 'CIRCLE': {
            const data: Record<string, unknown> = { center: point(10), radius: num(40, 'radius'), segments: 64 };
            if (color) data.color = color;
            return { type: 'circle', data, layerId, name: `Circle-${Date.now()}` };
        }
        case 'ARC': {
            const data: Record<string, unknown> = {
                center: point(10),
                radius: num(40, 'radius'),
                startAngle: degToRad(num(50, 'start angle')),
                endAngle: degToRad(num(51, 'end angle')),
            };
            if (color) data.color = color;
            return { type: 'arc', data, layerId, name: `Arc-${Date.now()}` };
        }
        case 'LWPOLYLINE': {
            const vertices: [number, number, number][] = [];
            const bulges: number[] = [];
            for (let i = 0; i < pairs.length; i++) {
                const p = pairs[i];
                if (p.code === 10) {
                    if (p.nums.length < 2) throw new LispError('entmake LWPOLYLINE: malformed vertex');
                    vertices.push([p.nums[0], p.nums[1], p.nums[2] ?? 0]);
                    // DXF order: the bulge (42) immediately follows its vertex.
                    const next = pairs[i + 1];
                    bulges.push(next && next.code === 42 && next.nums.length > 0 ? next.nums[0] : 0);
                }
            }
            if (vertices.length < 2) throw new LispError('entmake LWPOLYLINE: needs at least 2 vertices');
            const flags = pairNum(pairs, 70) ?? 0;
            const data: Record<string, unknown> = { vertices, closed: (flags & 1) === 1 };
            if (bulges.some((b) => b !== 0)) data.bulges = bulges;
            if (color) data.color = color;
            return { type: 'polyline', data, layerId, name: `Polyline-${Date.now()}` };
        }
        case 'POINT': {
            const data: Record<string, unknown> = { position: point(10) };
            if (color) data.color = color;
            return { type: 'point', data, layerId, name: `Point-${Date.now()}` };
        }
        case 'TEXT':
        case 'MTEXT': {
            const content = pairStr(pairs, 1);
            if (content === null) throw new LispError(`entmake ${type}: missing text (group 1)`);
            const data: Record<string, unknown> = { content, position: point(10), multiline: type === 'MTEXT' };
            const rotation = pairNum(pairs, 50);
            if (rotation !== null) data.rotation = degToRad(rotation);
            if (color) data.color = color;
            return { type: type === 'MTEXT' ? 'mtext' : 'text', data, layerId, name: `Text-${Date.now()}` };
        }
        default:
            throw new LispError(`entmake: entity type not supported yet (${type})`);
    }
}

// ---------------------------------------------------------------------------
// Entity → assoc list (entget)
// ---------------------------------------------------------------------------

function pairValue(code: number, value: LispValue): LispValue {
    return mkList([{ k: 'int', v: code }, value]);
}

function pointPair(code: number, p: [number, number, number] | number[] | null | undefined): LispValue | null {
    if (!p) return null;
    return mkList([
        { k: 'int', v: code },
        mkReal(p[0]),
        mkReal(p[1]),
        mkReal(p[2] ?? 0),
    ]);
}

function asVec3(v: unknown): [number, number, number] | null {
    if (Array.isArray(v) && v.length >= 2 && v.every((n) => typeof n === 'number')) {
        return [v[0], v[1], v[2] ?? 0];
    }
    return null;
}

function numPair(code: number, v: unknown): LispValue | null {
    return typeof v === 'number' && Number.isFinite(v) ? pairValue(code, mkReal(v)) : null;
}

/** Approximate group-code projection of one entity (entget). */
export function entityToAssocList(entity: Entity): LispValue {
    const app = useAppStore.getState();
    const handle = entity.id.replace(/^cad:/, '');
    const layerName = app.layers[entity.layer]?.name ?? '0';
    const items: LispValue[] = [
        pairValue(0, mkStr(entity.type.toUpperCase())),
        pairValue(5, mkStr(handle)),
        pairValue(8, mkStr(layerName)),
    ];
    const colorAci = hexToAci(entity.data?.color);
    if (colorAci !== null) items.push(pairValue(62, { k: 'int', v: colorAci }));

    const d = entity.data ?? {};
    switch (entity.type) {
        case 'line': {
            const start = pointPair(10, asVec3(d.start));
            const end = pointPair(11, asVec3(d.end));
            if (start) items.push(start);
            if (end) items.push(end);
            break;
        }
        case 'circle': {
            const center = pointPair(10, asVec3(d.center));
            const radius = numPair(40, d.radius);
            if (center) items.push(center);
            if (radius) items.push(radius);
            break;
        }
        case 'arc': {
            const center = pointPair(10, asVec3(d.center));
            const radius = numPair(40, d.radius);
            const startAngle = numPair(50, typeof d.startAngle === 'number' ? (d.startAngle * 180) / Math.PI : undefined);
            const endAngle = numPair(51, typeof d.endAngle === 'number' ? (d.endAngle * 180) / Math.PI : undefined);
            if (center) items.push(center);
            if (radius) items.push(radius);
            if (startAngle) items.push(startAngle);
            if (endAngle) items.push(endAngle);
            break;
        }
        case 'polyline': {
            const vertices = Array.isArray(d.vertices) ? d.vertices : [];
            items.push(pairValue(90, { k: 'int', v: vertices.length }));
            items.push(pairValue(70, { k: 'int', v: d.closed ? 1 : 0 }));
            vertices.forEach((v: unknown, i: number) => {
                const p = pointPair(10, asVec3(v));
                if (p) items.push(p);
                const bulges = Array.isArray(d.bulges) ? d.bulges[i] : 0;
                if (typeof bulges === 'number' && bulges !== 0) items.push(numPair(42, bulges)!);
            });
            break;
        }
        case 'point': {
            const p = pointPair(10, asVec3(d.position));
            if (p) items.push(p);
            break;
        }
        case 'text':
        case 'mtext': {
            items.push(pairValue(1, mkStr(typeof d.content === 'string' ? d.content : '')));
            const p = pointPair(10, asVec3(d.position));
            if (p) items.push(p);
            const rotation = numPair(50, typeof d.rotation === 'number' ? (d.rotation * 180) / Math.PI : undefined);
            if (rotation) items.push(rotation);
            break;
        }
        default:
            break;
    }
    return mkList(items);
}

// ---------------------------------------------------------------------------
// Bounding boxes (ssget W/C)
// ---------------------------------------------------------------------------

export interface Box2 {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

function boxOfPoints(points: Array<[number, number, number] | number[] | null | undefined>): Box2 | null {
    const valid = points.filter((p): p is [number, number, number] => Array.isArray(p) && p.length >= 2);
    if (valid.length === 0) return null;
    const xs = valid.map((p) => p[0]);
    const ys = valid.map((p) => p[1]);
    return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
}

/** Approximate XY bounding box of an entity (selection-window math). */
export function entityBox(entity: Entity): Box2 | null {
    const d = entity.data ?? {};
    switch (entity.type) {
        case 'line':
            return boxOfPoints([asVec3(d.start), asVec3(d.end)]);
        case 'circle':
        case 'arc': {
            const c = asVec3(d.center);
            if (!c || typeof d.radius !== 'number') return null;
            return {
                minX: c[0] - d.radius, minY: c[1] - d.radius,
                maxX: c[0] + d.radius, maxY: c[1] + d.radius,
            };
        }
        case 'polyline':
            return boxOfPoints(Array.isArray(d.vertices) ? d.vertices.map(asVec3) : []);
        case 'point':
        case 'text':
        case 'mtext':
            return boxOfPoints([asVec3(d.position)]);
        default:
            return null;
    }
}

function boxesInside(inner: Box2, outer: Box2): boolean {
    return (
        inner.minX >= outer.minX && inner.maxX <= outer.maxX &&
        inner.minY >= outer.minY && inner.maxY <= outer.maxY
    );
}

function boxesOverlap(a: Box2, b: Box2): boolean {
    return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}

/** ssget 'W' (window: fully inside) / 'C' (crossing: box overlaps). */
export function selectByWindow(a: [number, number], b: [number, number], crossing: boolean): string[] {
    const window: Box2 = {
        minX: Math.min(a[0], b[0]), minY: Math.min(a[1], b[1]),
        maxX: Math.max(a[0], b[0]), maxY: Math.max(a[1], b[1]),
    };
    const ids: string[] = [];
    for (const entity of cadEntities()) {
        const box = entityBox(entity);
        if (!box) continue;
        const hit = crossing ? boxesOverlap(box, window) : boxesInside(box, window);
        if (hit) ids.push(entity.id);
    }
    return ids;
}

/** All CAD drafting entities in insertion order (the cad: ledger). */
export function cadEntities(): Entity[] {
    const entities = useAppStore.getState().entities;
    return Object.values(entities).filter((e) => e.id.startsWith('cad:'));
}
